import React, { useState, useEffect } from 'react';
import { staffClient } from 'clients/staff';
import Table, { Column } from 'ui/Table';
import Button from 'ui/Button';
import Modal from 'ui/Modal';
import Input from 'ui/Input';
import Loading from 'ui/Loading';
import Section from 'ui/Section';
import { Badge, Switch } from 'ui/shadcn';
import type { BadgeProps } from 'ui/shadcn';
import { toast } from 'react-toastify';
import { PlusIcon } from '@heroicons/react/24/outline';

interface NewsItem {
    id: number;
    title: string;
    summary: string;
    category: string;
    date: string;
    is_active: boolean;
}

const CATEGORY_OPTIONS = [
    '회사소식', '수상', '인증', '프로젝트', '기타'
];

const NewsManager: React.FC = () => {
    const [items, setItems] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState<NewsItem | null>(null);
    const [form, setForm] = useState({
        title: '',
        summary: '',
        category: '회사소식',
        date: new Date().toISOString().slice(0, 10),
        is_active: true,
    });

    useEffect(() => { fetchItems(); }, []);

    const fetchItems = async () => {
        try {
            const res = await staffClient.get('/website-news');
            setItems(res.data);
        } catch (error) {
            toast.error('뉴스 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setEditing(null);
        setForm({ title: '', summary: '', category: '회사소식', date: new Date().toISOString().slice(0, 10), is_active: true });
        setShowModal(true);
    };

    const openEdit = (item: NewsItem) => {
        setEditing(item);
        setForm({
            title: item.title,
            summary: item.summary || '',
            category: item.category || '회사소식',
            date: item.date ? item.date.slice(0, 10) : '',
            is_active: item.is_active,
        });
        setShowModal(true);
    };

    const handleSave = async () => {
        try {
            if (editing) {
                await staffClient.patch(`/website-news/${editing.id}`, form);
                toast.success('뉴스가 수정되었습니다.');
            } else {
                await staffClient.post('/website-news', form);
                toast.success('뉴스가 추가되었습니다.');
            }
            setShowModal(false);
            fetchItems();
        } catch (error) {
            toast.error('저장에 실패했습니다.');
        }
    };

    const handleDelete = async (item: NewsItem) => {
        if (!window.confirm(`"${item.title}" 을(를) 삭제하시겠습니까?`)) return;
        try {
            await staffClient.delete(`/website-news/${item.id}`);
            toast.success('삭제되었습니다.');
            fetchItems();
        } catch (error) {
            toast.error('삭제에 실패했습니다.');
        }
    };

    const handleToggleActive = async (item: NewsItem) => {
        try {
            const newActive = !item.is_active;
            await staffClient.patch(`/website-news/${item.id}`, { is_active: newActive });
            setItems(prev => prev.map(n => n.id === item.id ? { ...n, is_active: newActive } : n));
            toast.success(newActive ? '활성화되었습니다.' : '비활성화되었습니다.');
        } catch (error) {
            toast.error('상태 변경에 실패했습니다.');
        }
    };

    const formatDate = (d: string) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('ko-KR');
    };

    const columns: Column<NewsItem>[] = [
        {
            key: 'date',
            header: '날짜',
            width: '110px',
            render: (value) => <span className="tabular-nums text-muted-foreground">{formatDate(value)}</span>,
        },
        {
            key: 'category',
            header: '카테고리',
            width: '100px',
            render: (value) => {
                const variants: Record<string, BadgeProps['variant']> = {
                    '회사소식': 'default',
                    '수상': 'warning',
                    '인증': 'success',
                    '프로젝트': 'info',
                };
                return <Badge variant={variants[value] || 'secondary'}>{value || '-'}</Badge>;
            },
        },
        {
            key: 'title',
            header: '제목',
            render: (value) => <span className="font-medium">{value}</span>,
        },
        {
            key: 'is_active',
            header: '노출',
            width: '70px',
            align: 'center',
            render: (value, row) => (
                <Switch
                    checked={!!value}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggleActive(row)}
                    aria-label="노출 여부"
                />
            ),
        },
    ];

    return (
        <div>
            <Section
                className="overflow-hidden"
                title={
                    <>
                        뉴스
                        {!loading && (
                            <span className="text-xs font-normal text-muted-foreground tabular-nums">
                                총 <span className="font-medium text-foreground">{items.length}</span>건 · 활성 {items.filter(n => n.is_active).length}건
                            </span>
                        )}
                    </>
                }
                description="홈페이지 유원소식 > 뉴스에 표시될 게시글을 관리합니다. 행을 클릭하면 수정할 수 있습니다."
                actions={
                    <Button variant="primary" icon={<PlusIcon />} onClick={openCreate}>
                        뉴스 추가
                    </Button>
                }
                flush
            >
                {loading ? (
                    <Loading text="뉴스를 불러오는 중..." />
                ) : (
                    <Table
                        columns={columns}
                        data={items}
                        pagination
                        pageSize={10}
                        emptyMessage="등록된 뉴스가 없습니다."
                        onRowClick={openEdit}
                        actionsColumnWidth="90px"
                        actions={(row) => (
                            <Button
                                size="small"
                                variant="danger-outline"
                                onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
                            >
                                삭제
                            </Button>
                        )}
                    />
                )}
            </Section>

            <Modal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                title={editing ? '뉴스 수정' : '뉴스 추가'}
            >
                <div className="form-group">
                    <label>제목 *</label>
                    <Input
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="뉴스 제목"
                    />
                </div>
                <div className="form-group">
                    <label>요약/내용</label>
                    <textarea
                        value={form.summary}
                        onChange={(e) => setForm({ ...form, summary: e.target.value })}
                        placeholder="뉴스 요약 내용"
                        rows={4}
                        className="w-full rounded border border-input bg-background px-3 py-2 text-base text-foreground placeholder:text-neutral-400 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring resize-none"
                    />
                </div>
                <div className="flex gap-4">
                    <div className="form-group flex-1">
                        <label>카테고리</label>
                        <select
                            value={form.category}
                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                            className="h-9 w-full rounded border border-input bg-background px-3 text-base text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
                        >
                            {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="form-group flex-1">
                        <label>날짜 *</label>
                        <Input
                            type="date"
                            value={form.date}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                        />
                    </div>
                </div>
                <div className="modal-footer">
                    <Button variant="primary" onClick={handleSave} disabled={!form.title || !form.date}>
                        {editing ? '수정' : '추가'}
                    </Button>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>취소</Button>
                </div>
            </Modal>
        </div>
    );
};

export default NewsManager;
